// Outcome badges drawn on the scan preview — the same right/wrong verdict the
// answer grid shows, placed beside each row on the photo of the paper so a
// teacher can read the sheet itself instead of cross-referencing the list.
//
// This lives in the UI, NOT in the engine's debug overlay, on purpose: the
// engine never sees an answer key (it reads the sheet, scoring interprets it),
// and its overlay rings mean something different — how confident the READ was.
// Rings = read confidence, filled badges = scoring outcome; keeping them
// visually distinct is what stops "low-confidence" reading as "wrong".
import { classifyOutcome } from '../scoring/score.js';
import { drawImageData } from './camera.js';
import { OUTCOME_MARKERS } from './answerGrid.js';
import type { AnswerKeyRecord } from '../db/schema.js';
import type { LayoutMap, ImageDataLike } from '../engine/index.js';
import type { GridItem } from './answerGrid.js';

/** Where normalized layout coords land in the warped image (engine meta). */
export interface ContentRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const FALLBACK: Record<string, string> = {
  correct: '#1a7f4b',
  wrong: '#b3272d',
  blank: '#6b6659',
  double: '#a4620f',
};

/** The --outcome-* token, so the badge and the grid marker can't drift apart. */
function outcomeColor(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--outcome-${name}`).trim();
  return v || FALLBACK[name]!;
}

/** Per item: the row's baseline (v) and the u of its last option bubble. */
function answerRows(layout: LayoutMap): Map<number, { v: number; uEnd: number }> {
  const rows = new Map<number, { v: number; uEnd: number }>();
  for (const b of layout.bubbles) {
    if (b.role !== 'answer') continue;
    const cur = rows.get(b.q);
    if (!cur) rows.set(b.q, { v: b.v, uEnd: b.u });
    else if (b.u > cur.uEnd) cur.uEnd = b.u;
  }
  return rows;
}

/**
 * Draw the engine's debug image, then a scoring badge beside each scored row.
 *
 * Badges sit just right of the last option bubble — the gutter between answer
 * blocks — so they never cover a bubble or the printed item number. Their size
 * follows the layout's bubble radius, so a dense 100-item sheet gets
 * proportionally smaller badges instead of overlapping ones. An item the key
 * doesn't cover (and a scan with no key at all) gets no badge, exactly like the
 * grid below it.
 */
export function drawSheetPreview(
  canvas: HTMLCanvasElement,
  img: ImageDataLike,
  rect: ContentRect | undefined,
  layout: LayoutMap,
  answers: readonly GridItem[],
  key: AnswerKeyRecord | null | undefined,
): void {
  // No key, or an engine run that never got a homography → photo only.
  if (!key || !rect) {
    drawImageData(canvas, img);
    return;
  }

  const rows = answerRows(layout);
  const bubbleR = layout.bubble_radius_norm * rect.w;
  const badgeR = Math.max(6, bubbleR * 0.85);
  const gap = bubbleR * 2.2;
  const at = (row: { v: number; uEnd: number }) => ({
    x: rect.x + row.uEnd * rect.w + gap,
    y: rect.y + row.v * rect.h,
  });

  // The right-most answer block on a dense preset runs close to the paper edge,
  // leaving no gutter for its badges. Rather than crowd them against the edge (or
  // shove them onto the bubbles), widen the canvas and let the sheet keep a
  // margin of its own — the preview is scaled to fit either way.
  let overflow = 0;
  for (const [, row] of rows) overflow = Math.max(overflow, at(row).x + badgeR + 2 - img.width);
  const pad = Math.max(0, Math.ceil(overflow));

  drawImageData(canvas, img);
  if (pad) {
    const strip = canvas.getContext('2d')!.getImageData(0, 0, img.width, img.height);
    canvas.width = img.width + pad;
    canvas.height = img.height;
    const c = canvas.getContext('2d')!;
    c.fillStyle = '#fff';
    c.fillRect(0, 0, canvas.width, canvas.height);
    c.putImageData(strip, 0, 0);
  }

  const ctx = canvas.getContext('2d')!;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${Math.round(badgeR * 1.45)}px system-ui, sans-serif`;
  ctx.lineWidth = Math.max(1, badgeR * 0.14);
  ctx.strokeStyle = '#fff';

  for (const a of answers) {
    const outcome = classifyOutcome(key, a.q, a);
    if (outcome === 'unscored') continue;
    const row = rows.get(a.q);
    if (!row) continue;
    const { x, y } = at(row);
    ctx.fillStyle = outcomeColor(outcome);
    ctx.beginPath();
    ctx.arc(x, y, badgeR, 0, Math.PI * 2);
    ctx.fill();
    // A hairline of paper around each badge keeps it legible where it lands on
    // printed rules or a dark photo.
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.fillText(OUTCOME_MARKERS[outcome].glyph, x, y + badgeR * 0.06);
  }
}
